import { useTranslation } from 'react-i18next';
import { Variant } from '@components/badge/src/badge.type';
import { IconDrag } from '@components/icons';
import { InputCheckbox } from '@components/input-checkbox';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import classNames from 'classnames';

import {
  AdmissionCardStatus,
  AdmissionCardTag,
  STATUS_TRANSLATION_KEYS,
  STATUS_VARIANT,
} from '../src/admission-card.type';

import { AdmissionCardBadges } from './admission-card-badges';

export const AdmissionCardInfo = ({
  id,
  status,
  checked,
  isPreset,
  listeners,
  attributes,
  isDragActive = true,
  isMultiSelectActive,
  onChange,
  cardOpacity,
  color,
  name,
  tags,
  accessValidity,
  locale,
  guestCapacity,
  soldItems,
  quantity,
  description,
  isDateVisible,
  onlinePrice,
  ddpPrice,
  formattedSalesEndsDate,
  formattedOnlinePrice,
  formatedDdpPrice,
}: {
  id: string;
  tags: AdmissionCardTag[];
  locale: string;
  soldItems: number;
  quantity: number | null;
  cardOpacity: string;
  checked?: boolean;
  isPreset?: boolean;
  status?: AdmissionCardStatus;
  listeners?: SyntheticListenerMap;
  accessValidity?: string;
  guestCapacity?: number;
  attributes?: DraggableAttributes;
  isDragActive?: boolean;
  isMultiSelectActive?: boolean;
  onChange?: (e: boolean) => void;
  color?: string | null;
  name?: string;
  description?: string | null;
  isDateVisible: boolean;
  onlinePrice?: number;
  ddpPrice?: number;
  formattedSalesEndsDate?: string;
  formattedOnlinePrice?: string;
  formatedDdpPrice?: string;
}) => {
  const { t } = useTranslation();
  const badgeVariant = status ? (STATUS_VARIANT[status] as Variant) : '';
  const statusTranslated = status ? t(STATUS_TRANSLATION_KEYS[status]) : '';

  return (
    <>
      {isDragActive && (
        <div
          {...listeners}
          {...attributes}
          style={{ touchAction: 'none' }}
          className='flex items-center pr-3.75 cursor-move'>
          <IconDrag className='text-tertiary w-5 h-5' />
        </div>
      )}
      {isMultiSelectActive && (
        <div
          className={classNames('flex items-center pr-3.75', {
            'pl-1': isPreset,
          })}>
          <InputCheckbox name={id} value={!!checked} type='checkbox' rounded={true} onChange={onChange} />
        </div>
      )}
      <div className={`flex flex-col ${cardOpacity} w-full gap-2`}>
        <div className='flex flex-row items-center'>
          {color && <div className='w-3 h-3 rounded-full mr-1.5 -mt-0.5' style={{ backgroundColor: `${color}` }}></div>}
          <span data-testid='admission-card--name' className='text-left text-subtitle-m text-primary'>
            {name}
          </span>
        </div>
        {(description || isDateVisible) && (
          <div className='flex flex-col gap-1 overflow-hidden'>
            {description && (
              <p
                className={classNames(
                  'hidden sm:block text-secondary text-body-xs whitespace-nowrap overflow-hidden overflow-ellipsis',
                  {
                    'w-[95%]': !isPreset,
                    'w-[99%] text-left': isPreset,
                  }
                )}>
                {String(description).slice(0, 250)}
              </p>
            )}
            {(!!onlinePrice || !!ddpPrice || isDateVisible) && (
              <p className='flex items-center gap-2'>
                {isDateVisible && (
                  <span className='hidden sm:block text-body-xs text-tertiary'>
                    {t('common:admissionCardSalesEndsAt', {
                      time: formattedSalesEndsDate,
                    })}
                  </span>
                )}
                {!!onlinePrice && (
                  <>
                    {isDateVisible && <span className='hidden sm:block text-body-xs text-tertiary'>|</span>}
                    <span className='text-body-xs text-tertiary'>
                      {t('offers:downpayment')}: {formattedOnlinePrice}
                    </span>
                  </>
                )}
                {!!ddpPrice && (
                  <>
                    {(isDateVisible || !!onlinePrice) && (
                      <span
                        className={classNames('text-body-xs text-tertiary', {
                          'hidden sm:block': !onlinePrice,
                        })}>
                        |
                      </span>
                    )}
                    <span className='text-body-xs text-tertiary'>{`${t('offers:admissionDDPLabel')}: ${formatedDdpPrice}`}</span>
                  </>
                )}
              </p>
            )}
          </div>
        )}
        <AdmissionCardBadges
          {...{
            status,
            badgeVariant,
            statusTranslated,
            tags,
            accessValidity,
            locale,
            guestCapacity,
            cardOpacity,
            soldItems,
            quantity,
            t,
          }}
        />
      </div>
    </>
  );
};
